import {
  buildProxiedInstance,
  expandAllFeature,
  hotkeysCoreFeature,
  syncDataLoaderFeature,
} from "@headless-tree/core";
import { useTree } from "@headless-tree/react";
import { InputAdornment, TextField } from "@mui/material";
import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import * as React from "react";

import { ChevronRight, Search } from "@/shared/foundation/icons";
import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";
import { substringFilter } from "@/shared/text/substringFilter";
import { SubstringHighlight } from "@/shared/text/SubstringHighlight";
import { buildPathTree, type PathTreeNode } from "@/shared/tree/pathTree";

import { type MetricFilterOption } from "./MetricFilterForm";
import styles from "./MetricTree.module.css";

const SEARCH_DEBOUNCE_MS = 150;

// Fixed row height for the search-results virtualizer. Rows are a single
// middle-truncated line of fixed height, so a constant `estimateSize` is exact
// — no per-row measurement. Must match `.result-row` in MetricTree.module.css.
const RESULT_ROW_HEIGHT = 30;

// Trailing characters of the name kept pinned (never truncated) when the row
// middle-truncates, so the end — the field tail and the stat — stays visible.
const RESULT_ROW_TAIL_CHARS = 18;

// Fixed row height for the browse-tree virtualizer. Browse rows are single-line;
// `.browse-row` pins the height so `estimateSize` is exact.
const BROWSE_ROW_HEIGHT = 30;

/** A node of the browse tree; each leaf is a metric's name. */
type MetricNode = PathTreeNode<string>;

/** One level per dotted part of a metric's name. */
const metricNamePath = (name: string) => name.split(".");
const metricNameId = (name: string) => name;

/**
 * headless-tree's sync data loader must return an item for *every* id it tracks.
 * The tree returns `undefined` for ids it doesn't know — which happens
 * transiently when retained tree state (focus/expansion) references a node a
 * rebuild has just pruned. This placeholder keeps the loader total so the tree
 * never reads a property off `undefined`. It isn't rendered (the stale row is
 * reconciled away on the synchronous rebuild), so the blank label is fine.
 */
function placeholderMetricNode(id: string): MetricNode {
  return { kind: "group", id, path: "", label: "", depth: 0, leaves: [] };
}

export interface MetricTreeProps {
  /** Full set of selectable metrics (name + unit). The tree structure is derived
   *  client-side from their dot-delimited names. */
  metrics: MetricFilterOption[];
  /** The currently-selected metric, or null. Highlights the matching leaf. */
  selectedMetric: MetricFilterOption | null;
  /** Called when the user picks a leaf metric. */
  onSelect: (metric: MetricFilterOption) => void;
}

/**
 * Left pane of the metrics filter: a debounced search box over two mutually
 * exclusive modes. A non-empty query renders `MetricSearchResults` (a flat,
 * virtualized, ranked list); an empty query renders `MetricBrowseTree`
 * (the collapsible, virtualized headless-tree). Each mode owns its own data and
 * empty state, so this component is just the input and the switch between them.
 */
export function MetricTree({
  metrics,
  selectedMetric,
  onSelect,
}: MetricTreeProps) {
  // The tree and fuzzy search are name-derived, so the subtrees work in names.
  // These map between that internal name view and the { name, unit } option the
  // public API speaks in: `names` feeds the tree/search, `selectName` resolves a
  // picked name back to its option, and `selectedName` drives row highlighting.
  const names = React.useMemo(() => metrics.map((m) => m.name), [metrics]);
  const optionByName = React.useMemo(
    () => new Map(metrics.map((m) => [m.name, m])),
    [metrics],
  );
  const selectName = React.useCallback(
    (name: string) => {
      const option = optionByName.get(name);
      if (option) {
        onSelect(option);
      }
    },
    [optionByName, onSelect],
  );
  const selectedName = selectedMetric?.name ?? null;

  const [query, setQuery] = React.useState("");
  const debouncedSetQuery = useDebouncedCallback(setQuery, SEARCH_DEBOUNCE_MS);

  // MUI Popover's Grow transition clobbers `autoFocus` on child inputs; claim
  // focus one frame after mount instead.
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(function focusSearchInput() {
    const handle = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(handle);
  }, []);

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Enter") {
      return;
    }
    const value = event.currentTarget.value;
    const matches = substringFilter(value, names);
    if (matches.length === 0) {
      return;
    }
    event.preventDefault();
    // Flush the debounce so the results reflect the same text the match was
    // computed from; pressing Enter before the 150ms debounce settles would
    // otherwise select a metric not yet visible in the (stale) list. `matches`
    // is ranked best-first, so `[0]` is the strongest match.
    debouncedSetQuery.cancel();
    setQuery(value);
    selectName(matches[0]);
  };

  const isSearching = query.trim().length > 0;

  return (
    <div className={styles.picker}>
      <div className={styles.search}>
        <TextField
          inputRef={searchInputRef}
          autoComplete="off"
          size="small"
          type="search"
          className={styles.searchInput}
          placeholder="Search metrics…"
          onChange={(event) => debouncedSetQuery(event.target.value)}
          inputProps={{
            "aria-label": "Search metrics",
            onKeyDown: handleSearchKeyDown,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={14} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {isSearching ? (
        <MetricSearchResults
          query={query}
          names={names}
          selectedName={selectedName}
          onSelect={selectName}
        />
      ) : (
        <MetricBrowseTree
          names={names}
          selectedName={selectedName}
          onSelect={selectName}
        />
      )}
    </div>
  );
}

interface MetricSearchResultsProps {
  /** The active search query. Ranks the names and highlights the matched substring. */
  query: string;
  /** Full list of metric names to search. */
  names: string[];
  /** The currently-selected metric path, or null. */
  selectedName: string | null;
  /** Called with the picked metric path. */
  onSelect: (name: string) => void;
}

/**
 * Search mode: a flat, virtualized list of the substring matches, ranked
 * best-first. Unlike browse it renders no hierarchy — each row is the full
 * dotted path, middle-truncated. Only the visible window is in the DOM, so the
 * row count is bounded regardless of how many names match. Owns the search
 * scan, the count line, and the empty state.
 */
function MetricSearchResults({
  query,
  names,
  selectedName,
  onSelect,
}: MetricSearchResultsProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  // Ranked best-first. The search scan runs here (search mode only), never on
  // the idle browse path.
  const results = React.useMemo(
    () => substringFilter(query, names),
    [query, names],
  );

  const virtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => RESULT_ROW_HEIGHT,
    // Stable key so React reconciliation survives re-ranking as the query grows.
    getItemKey: (index) => results[index],
    overscan: 8,
  });

  if (results.length === 0) {
    return <div className={styles.empty}>No matching metrics</div>;
  }

  const virtualRows = virtualizer.getVirtualItems();

  return (
    <>
      <div className={styles.resultCount} role="status">
        {`${results.length} of ${names.length} metrics`}
      </div>
      <div ref={scrollRef} className={styles.tree}>
        <div
          style={{ height: virtualizer.getTotalSize(), position: "relative" }}
        >
          {/* One transform on the window wrapper: rows are contiguous and fixed
              height, so per-row absolute positioning isn't needed. */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualRows[0]?.start ?? 0}px)`,
            }}
          >
            {virtualRows.map((virtualRow) => {
              const name = results[virtualRow.index];
              // One middle-truncated line: a head that shrinks + end-ellipsizes
              // next to a pinned tail, so both the name's start (namespace root)
              // and its end (field tail + stat) survive whatever the delimiter.
              // Both SubstringHighlights match the full `name` but render one slice;
              // `aria-label`/`title` carry the whole path.
              const tailStart = Math.max(
                0,
                name.length - RESULT_ROW_TAIL_CHARS,
              );
              const hasHead = tailStart > 0;
              return (
                <button
                  key={virtualRow.key}
                  className={classNames(styles.resultRow, {
                    [styles.rowSelected]: name === selectedName,
                  })}
                  onClick={() => onSelect(name)}
                  aria-label={name}
                  title={name}
                >
                  <span className={styles.resultLine}>
                    {hasHead && (
                      <span className={styles.resultHead}>
                        <SubstringHighlight
                          text={name}
                          query={query}
                          slice={[0, tailStart]}
                          markClassName={styles.mark}
                        />
                      </span>
                    )}
                    <span className={styles.resultTail}>
                      <SubstringHighlight
                        text={name}
                        query={query}
                        slice={[tailStart, name.length]}
                        markClassName={styles.mark}
                      />
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

interface MetricBrowseTreeProps {
  /** Full list of metric names; the tree structure is derived from these. */
  names: string[];
  /** The currently-selected metric path, or null. Highlights the matching leaf. */
  selectedName: string | null;
  /** Called with the picked leaf's metric path. */
  onSelect: (name: string) => void;
}

/**
 * Browse mode: a collapsible, virtualized headless-tree of the dot-delimited
 * names. The tree flattens to `getItems()`, which we window so a wide top level
 * or a wide expansion never floods the DOM.
 *
 * Owns the tree, its data, and the empty state.
 */
function MetricBrowseTree({
  names,
  selectedName,
  onSelect,
}: MetricBrowseTreeProps) {
  // Derived from the full name set (browse never filters), so it is stable
  // across renders and built once per name-set change.
  const index = React.useMemo(
    () =>
      buildPathTree({
        leaves: names,
        getPath: metricNamePath,
        getId: metricNameId,
      }),
    [names],
  );

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const virtualizerRef = React.useRef<Virtualizer<
    HTMLDivElement,
    Element
  > | null>(null);

  const tree = useTree<MetricNode>({
    rootItemId: index.rootId,
    getItemName: (item) => item.getItemData().label,
    isItemFolder: (item) => item.getItemData().kind === "group",
    dataLoader: {
      getItem: (id) => index.getNode(id) ?? placeholderMetricNode(id),
      getChildren: index.getChildren,
    },
    onPrimaryAction: (item) => {
      if (item.getItemData().kind === "leaf") {
        onSelect(item.getItemData().id);
      }
    },
    // Proxied instances avoid building a full instance per node on large trees.
    instanceBuilder: buildProxiedInstance,
    scrollToItem: (item) =>
      virtualizerRef.current?.scrollToIndex(item.getItemMeta().index),
    features: [syncDataLoaderFeature, hotkeysCoreFeature, expandAllFeature],
  });

  React.useEffect(
    function rebuildTree() {
      tree.rebuildTree();
    },
    [index, tree],
  );

  const virtualizer = useVirtualizer({
    count: tree.getItems().length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => BROWSE_ROW_HEIGHT,
    getItemKey: (i) => tree.getItems()[i]?.getId() ?? i,
    overscan: 8, // Chosen arbitrarily
  });
  // Expose the virtualizer to the tree's `scrollToItem` (defined above).
  virtualizerRef.current = virtualizer;

  if (names.length === 0) {
    return <div className={styles.empty}>No metrics defined</div>;
  }

  const rows = virtualizer.getVirtualItems();

  return (
    // The scroll element is the outer div; getContainerProps (role=tree +
    // keyboard) sits on the full-height inner container so tree items stay its
    // descendants.
    <div ref={scrollRef} className={styles.tree}>
      <div
        {...tree.getContainerProps("Metrics")}
        style={{ height: virtualizer.getTotalSize(), position: "relative" }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${rows[0]?.start ?? 0}px)`,
          }}
        >
          {rows.map((virtualRow) => {
            const item = tree.getItems()[virtualRow.index];
            const node = item.getItemData();
            const isFolder = item.isFolder();
            return (
              <button
                key={item.getId()}
                {...item.getProps()}
                className={classNames(styles.browseRow, {
                  [styles.rowSelected]:
                    node.kind === "leaf" && node.id === selectedName,
                })}
                style={
                  {
                    "--indent-depth": item.getItemMeta().level,
                  } as React.CSSProperties
                }
              >
                {isFolder ? (
                  <ChevronRight
                    className={classNames(styles.chevron, {
                      [styles.chevronOpen]: item.isExpanded(),
                    })}
                    aria-hidden="true"
                  />
                ) : (
                  <span className={styles.iconSpacer} aria-hidden="true" />
                )}
                {/* Native title shows the full dotted path on hover so a
                    truncated label is still readable. */}
                <span
                  className={classNames(styles.label, "truncate")}
                  title={node.path}
                >
                  {node.label}
                </span>
                {node.kind === "group" && (
                  <span className={styles.count}>{node.leaves.length}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
